import api from '../services/api';

export const printJobSheet = async (jobSheet, onStatusChange) => {
  // If job sheet is in "Pending" status, automatically update to "In Progress" when printing
  if (jobSheet.status === 'Pending') {
    try {
      await api.put(`/job-sheets/${jobSheet.id}`, {
        status: 'In Progress'
      });
      // Refresh the job sheets list to reflect the status change
      if (onStatusChange) await onStatusChange();
      // Update the local jobSheet object for printing
      jobSheet = { ...jobSheet, status: 'In Progress' };
    } catch (error) {
      console.error('Error updating job sheet status:', error);
      // Continue with printing even if status update fails
    }
  }
  
  // Fetch Q.A. items from the linked quote if not on job sheet
  // This handles job sheets created before Q.A. items were added to the quote
  let qaInspectionItems = jobSheet.qa_inspection_items || [];
  let inspectionInterval = jobSheet.inspection_interval;
  
  if (qaInspectionItems.length === 0 && jobSheet.quote_id) {
    try {
      const quoteResponse = await api.get(`/quotes/${jobSheet.quote_id}`);
      const quote = quoteResponse.data;
      if (quote.qa_inspection_items && quote.qa_inspection_items.length > 0) {
        qaInspectionItems = quote.qa_inspection_items;
        // Update the job sheet with Q.A. items for future prints
        await api.put(`/job-sheets/${jobSheet.id}`, {
          qa_inspection_items: qaInspectionItems
        });
      }
    } catch (error) {
      console.error('Error fetching quote Q.A. items:', error);
    }
  }
  
  // Format due date as "28 NOV 2025"
  const formatDueDateForPrint = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate();
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };
  
  // Calculate total time for the job
  const numberOfBends = jobSheet.number_of_bends || 0;
  const secondsPerBend = jobSheet.time_per_bend || 30;
  const packingTime = jobSheet.packing_time || 0;
  const quantity = jobSheet.quantity || 1;
  const setupTimeMinutes = jobSheet.setup_time || 0;
  
  // Total bend time in minutes
  const totalBendTimeMinutes = (numberOfBends * secondsPerBend * quantity) / 60;
  // Total packing time in minutes
  const totalPackingTimeMinutes = (packingTime * quantity) / 60;
  // Total job time in minutes
  const totalJobTimeMinutes = setupTimeMinutes + totalBendTimeMinutes + totalPackingTimeMinutes;
  // Convert to hours and minutes
  const totalJobHours = Math.floor(totalJobTimeMinutes / 60);
  const totalJobMinutes = Math.round(totalJobTimeMinutes % 60);
  
  const printWindow = window.open('', '_blank');
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Job Sheet ${jobSheet.job_sheet_number}</title>
        <style>
          @page {
            size: A4;
            margin: 6mm;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 0;
            margin: 0;
            font-size: 9pt;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 5px;
            margin-bottom: 10px;
            position: relative;
          }
          .header h1 {
            margin: 0;
            font-size: 16pt;
            color: #333;
          }
          .header h2 {
            color: #666;
            margin: 3px 0;
            font-size: 12pt;
          }
          .qr-code {
            position: absolute;
            top: 0;
            right: 0;
            text-align: center;
          }
          .qr-code img {
            width: 80px;
            height: 80px;
          }
          .qr-code-label {
            font-size: 7pt;
            color: #666;
            margin-top: 2px;
          }
          .section {
            margin-bottom: 8px;
            border: 1px solid #ddd;
            padding: 6px;
            border-radius: 3px;
            page-break-inside: avoid;
          }
          .section h2 {
            margin: 0 0 4px 0;
            color: #555;
            border-bottom: 1px solid #ddd;
            padding-bottom: 3px;
            font-size: 10pt;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 5px;
            font-size: 8.5pt;
          }
          .info-item {
            padding: 2px 0;
          }
          .label {
            font-weight: bold;
            color: #666;
          }
          .notes-box {
            background: #f9f9f9;
            border: 1px solid #ddd;
            padding: 6px;
            border-radius: 3px;
            min-height: 30px;
            font-size: 8.5pt;
          }
          .status-badge {
            display: inline-block;
            padding: 3px 10px;
            border-radius: 10px;
            font-weight: bold;
            font-size: 9pt;
          }
          .status-pending { background: #fef3c7; color: #92400e; }
          .status-in-progress { background: #dbeafe; color: #1e40af; }
          .status-completed { background: #d1fae5; color: #065f46; }
          .status-on-hold { background: #fee2e2; color: #991b1b; }
          .compact-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 5px;
            font-size: 8.5pt;
          }
          .bullet-list {
            margin: 5px 0 5px 15px;
            padding: 0;
            font-size: 8.5pt;
            line-height: 1.4;
          }
          .bullet-list li {
            margin-bottom: 3px;
          }
          table.form-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8.5pt;
          }
          table.form-table td {
            border: 1px solid #ddd;
            padding: 4px;
          }
          .underline-field {
            border-bottom: 1px solid #333;
            display: inline-block;
            min-width: 100px;
          }
          .highlight-box {
            background: #fef3c7;
            border: 2px solid #f59e0b;
            padding: 8px;
            margin: 8px 0;
            border-radius: 5px;
            text-align: center;
          }
          .highlight-box .big-field {
            font-size: 18pt;
            font-weight: bold;
            margin: 3px 0;
            color: #333;
          }
          .highlight-box .label-big {
            font-size: 9pt;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .key-info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin: 8px 0;
          }
          .key-info-item {
            background: #f0f9ff;
            border: 2px solid #3b82f6;
            padding: 6px;
            border-radius: 4px;
            text-align: center;
          }
          .key-info-item .label-med {
            font-size: 8pt;
            color: #1e40af;
            font-weight: bold;
            text-transform: uppercase;
          }
          .key-info-item .value-med {
            font-size: 14pt;
            font-weight: bold;
            color: #1e3a8a;
            margin-top: 3px;
          }
          .part-box {
            background: #f8fafc;
            border: 2px solid #cbd5e1;
            padding: 6px;
            border-radius: 4px;
            margin-bottom: 8px;
          }
          .part-box .part-label {
            font-weight: bold;
            font-size: 8pt;
            color: #475569;
            margin-bottom: 3px;
          }
          .part-box .part-value {
            font-size: 11pt;
            font-weight: bold;
            color: #1e293b;
          }
          @page {
            size: A4;
            margin: 0;
          }
          @media print {
            button { display: none; }
            body { 
              print-color-adjust: exact; 
              -webkit-print-color-adjust: exact;
              zoom: 102%;
              -moz-transform: scale(1.02);
              -moz-transform-origin: 0 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="qr-code">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(jobSheet.job_sheet_number)}" alt="QR Code" />
            <div class="qr-code-label">Scan to Complete</div>
          </div>
          <h1>JOB SHEET</h1>
          <h2>${jobSheet.job_sheet_number}</h2>
          <span class="status-badge status-${jobSheet.status.toLowerCase().replace(' ', '-')}">${jobSheet.status}</span>
        </div>

        <!-- PROMINENT KEY INFORMATION -->
        <div class="highlight-box">
          <div class="label-big">Quantity Required</div>
          <div class="big-field" style="color: #16a34a;">${jobSheet.quantity} PARTS</div>
        </div>

        <div class="key-info-grid">
          <div class="key-info-item">
            <div class="label-med">Process</div>
            <div class="value-med">${jobSheet.process_name}</div>
          </div>
          ${jobSheet.machine_name ? `
          <div class="key-info-item" style="background: #f3e8ff; border-color: #9333ea;">
            <div class="label-med" style="color: #6b21a8;">Machine</div>
            <div class="value-med" style="color: #6b21a8; font-weight: 900;">${jobSheet.machine_name}</div>
          </div>` : ''}
          <div class="key-info-item" style="background: #fef3c7; border-color: #f59e0b;">
            <div class="label-med" style="color: #92400e;">Due Date</div>
            <div class="value-med" style="color: #92400e;">${formatDueDateForPrint(jobSheet.due_date)}</div>
          </div>
        </div>

        <div class="part-box">
          <div class="part-label">PART DESCRIPTION:</div>
          <div class="part-value">${jobSheet.part_description}</div>
        </div>

        <div class="section">
          <h2>📋 Job Information</h2>
          <div class="info-grid">
            <div class="info-item"><span class="label">Quote:</span> ${jobSheet.quote_number}</div>
            <div class="info-item"><span class="label">Customer:</span> ${jobSheet.customer_name}</div>
            <div class="info-item"><span class="label">PO #:</span> ${jobSheet.purchase_order_number || 'N/A'}</div>
            <div class="info-item"><span class="label">Drawing:</span> ${jobSheet.drawing_number || 'N/A'}</div>
          </div>
        </div>

        <div class="section" style="background: #fff8dc; border: 2px solid #d4a017; padding: 10px; border-radius: 4px;">
          <h2 style="color: #8b6914; margin-top: 0;">🧱 MATERIAL SPECIFICATION — VERIFY BEFORE FOLDING</h2>
          <table class="form-table" style="font-size: 13px;">
            <tr>
              <td style="width: 50%;"><span class="label">Material Type:</span> <strong>${jobSheet.material_type || jobSheet.material || 'N/A'}</strong></td>
              <td><span class="label">Grade:</span> <strong>${jobSheet.grade || 'N/A'}</strong></td>
            </tr>
            <tr>
              <td><span class="label">Thickness:</span> <strong>${jobSheet.thickness || 'N/A'}</strong></td>
              <td><span class="label">Finish:</span> <strong>${jobSheet.finish || 'N/A'}</strong></td>
            </tr>
          </table>
        </div>

        ${(jobSheet.processes && jobSheet.processes.length > 0) || jobSheet.setup_time || jobSheet.number_of_bends ? `
        <div class="section">
          <h2>🔧 Press Brake Specifications</h2>
          
          ${(jobSheet.last_used_top_punch || jobSheet.last_used_v_die) ? `
          <div style="background: #f3e8ff; border: 2px solid #9333ea; border-radius: 6px; padding: 8px 12px; margin-bottom: 12px;">
            <div style="font-weight: 900; color: #6b21a8; font-size: 13px;">🔧 TOOLING — AS USED ON LAST RUN (${jobSheet.last_run_job_number || 'previous job'}${jobSheet.last_run_machine ? ` — ${jobSheet.last_run_machine}` : ''})</div>
            <div style="font-weight: 800; color: #111; font-size: 14px; margin-top: 2px;">Top Punch: ${jobSheet.last_used_top_punch || '—'} &nbsp;|&nbsp; V Die: ${jobSheet.last_used_v_die || '—'}</div>
            <div style="font-size: 10px; color: #6b21a8;">Supersedes quote tooling if different.</div>
          </div>` : ''}
          
          ${jobSheet.processes && jobSheet.processes.length > 0 ? `
            ${jobSheet.processes.map((process, index) => `
              <div style="margin-bottom: 20px; ${index > 0 ? 'border-top: 1px solid #ddd; padding-top: 15px;' : ''}">
                <h3 style="margin-bottom: 10px; color: #2563EB;">Process ${index + 1}: ${process.process_name || 'Press Brake'}</h3>
                <table class="form-table">
                  <tr>
                    <td style="width: 50%;"><span class="label">Setup Time (min):</span> ${process.setup_time || 'N/A'}</td>
                    <td><span class="label">Material:</span> ${process.material || jobSheet.material || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><span class="label">Thickness (mm):</span> ${process.thickness || 'N/A'}</td>
                    <td><span class="label">No. of Bends:</span> ${process.number_of_bends || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td colspan="2"><span class="label">🔧 Tooling Required:</span> <strong>${(jobSheet.last_used_top_punch || jobSheet.last_used_v_die) ? `${[jobSheet.last_used_top_punch ? `Top Punch: ${jobSheet.last_used_top_punch}` : '', jobSheet.last_used_v_die ? `V Die: ${jobSheet.last_used_v_die}` : ''].filter(Boolean).join(' | ')} (as used on ${jobSheet.last_run_job_number})` : (process.tooling_required || 'Not specified')}</strong></td>
                  </tr>
                  <tr>
                    <td><span class="label">Bend Angles:</span> ${process.bend_angles || '___________________________'}</td>
                    <td><span class="label">Tonnage:</span> ${process.tonnage || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><span class="label">Time per Part (sec):</span> ${process.time_per_part || 'N/A'}</td>
                    <td><span class="label">Bend Length (mm):</span> ${process.bend_length || 'N/A'}</td>
                  </tr>
                </table>
              </div>
            `).join('')}
          ` : `
            <table class="form-table">
              <tr>
                <td style="width: 50%;"><span class="label">Setup Time (min):</span> ${setupTimeMinutes}</td>
                <td><span class="label">Material Type:</span> ${jobSheet.material}</td>
              </tr>
              <tr>
                <td><span class="label">🔧 Tooling Required:</span> <strong>${(jobSheet.last_used_top_punch || jobSheet.last_used_v_die) ? `${[jobSheet.last_used_top_punch ? `Top Punch: ${jobSheet.last_used_top_punch}` : '', jobSheet.last_used_v_die ? `V Die: ${jobSheet.last_used_v_die}` : ''].filter(Boolean).join(' | ')} (as used on ${jobSheet.last_run_job_number})` : (jobSheet.tooling_required || 'Not specified')}</strong></td>
                <td><span class="label">No. of Bends:</span> ${numberOfBends}</td>
              </tr>
              <tr>
                <td><span class="label">Bend Angles:</span> ${jobSheet.bend_angles || '___________________________'}</td>
                <td><span class="label">Tonnage:</span> ${jobSheet.tonnage || 'N/A'}</td>
              </tr>
            </table>
          `}
        </div>

        ${jobSheet.previous_production_notes ? `
        <div class="section">
          <h2>📝 Notes From Previous Runs</h2>
          <div style="border: 2px solid #f59e0b; background: #fffbeb; border-radius: 6px; padding: 8px 12px; white-space: pre-wrap; font-size: 12px; font-weight: 600;">${jobSheet.previous_production_notes}</div>
        </div>` : ''}

        <div class="section">
          <h2>⏱️ Time Tracking</h2>
          <table class="form-table">
            <tr>
              <td><span class="label">Setup Time:</span> ${setupTimeMinutes} min</td>
              <td><span class="label">Total Job Time:</span> ${totalJobHours}h ${totalJobMinutes}m (Est.)</td>
            </tr>
            <tr>
              <td><span class="label">Started:</span> <span class="underline-field"></span></td>
              <td><span class="label">Finished:</span> <span class="underline-field"></span></td>
            </tr>
            <tr>
              <td colspan="2"><span class="label">Operator:</span> <span class="underline-field" style="min-width: 300px;"></span></td>
            </tr>
          </table>
        </div>
        ` : `
        <div class="section">
          <h2>⏱️ Time Tracking</h2>
          <table class="form-table">
            <tr>
              <td><span class="label">Setup Time:</span> ${setupTimeMinutes} min</td>
              <td><span class="label">Total Job Time:</span> ${totalJobHours}h ${totalJobMinutes}m (Est.)</td>
            </tr>
            <tr>
              <td><span class="label">Started:</span> <span class="underline-field"></span></td>
              <td><span class="label">Finished:</span> <span class="underline-field"></span></td>
            </tr>
            <tr>
              <td colspan="2"><span class="label">Operator:</span> <span class="underline-field" style="min-width: 300px;"></span></td>
            </tr>
          </table>
        </div>
        `}

        ${jobSheet.process_notes ? `
        <div class="section">
          <h2>📝 Operator Awareness / Special Instructions</h2>
          <ul class="bullet-list">
            ${jobSheet.process_notes.split('\\n').map(note => note.trim()).filter(note => note).map(note => `<li>${note}</li>`).join('')}
          </ul>
        </div>
        ` : ''}

        <div class="section">
          <h2>📦 Packing / Handling</h2>
          <table class="form-table">
            <tr>
              <td style="width: 40%;"><span class="label">Pack Quantity:</span> <span class="underline-field"></span></td>
              <td><span class="label">Protection:</span> <span class="underline-field"></span></td>
            </tr>
            <tr>
              <td><span class="label">Pallet / Box No:</span> <span class="underline-field"></span></td>
              <td><span class="label">Packed By:</span> <span class="underline-field"></span></td>
            </tr>
          </table>
        </div>

        <div class="section">
          <h2>✅ Quality Check & Rejects</h2>
          <table class="form-table">
            <tr>
              <td style="width: 50%;"><strong>☐ Passed  ☐ Failed</strong> &nbsp; Inspector: <span class="underline-field"></span></td>
              <td><span class="label">Date:</span> <span class="underline-field"></span></td>
            </tr>
            <tr>
              <td><span class="label">Parts Produced:</span> <span class="underline-field"></span></td>
              <td><span class="label">Rejects / Scrap:</span> <span class="underline-field"></span></td>
            </tr>
            <tr>
              <td colspan="2"><span class="label">Reject Reason:</span> <span class="underline-field" style="min-width: 400px;"></span></td>
            </tr>
          </table>
        </div>

        <div class="section">
          <h2>🖊️ Sign-Off</h2>
          <table class="form-table">
            <tr>
              <td style="width: 50%;"><span class="label">Completed By:</span> <span class="underline-field" style="min-width: 200px;"></span></td>
              <td><span class="label">Date:</span> <span class="underline-field"></span></td>
            </tr>
            <tr>
              <td colspan="2"><span class="label">Signature:</span> <span class="underline-field" style="min-width: 300px;"></span></td>
            </tr>
          </table>
        </div>

        ${/* PAGE 2: Dimensional Inspection Checklist - Only shown if Q.A. items exist */
        qaInspectionItems && qaInspectionItems.length > 0 ? `
        <div class="page-break" style="page-break-before: always;"></div>
        
        <!-- PAGE 2: DIMENSIONAL INSPECTION CHECKLIST -->
        <div class="header" style="margin-top: 10px;">
          <h1>DIMENSIONAL INSPECTION CHECKLIST</h1>
          <h2>Page 2 of Job Sheet ${jobSheet.job_sheet_number}</h2>
        </div>
        
        <div class="section">
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
            <tr>
              <td style="width: 50%; padding: 4px;"><span class="label">Job Number:</span> <strong>${jobSheet.job_sheet_number}</strong></td>
              <td style="width: 50%; padding: 4px;"><span class="label">Drawing Number:</span> <strong>${jobSheet.drawing_number || 'N/A'}</strong></td>
            </tr>
            <tr>
              <td style="padding: 4px;"><span class="label">Customer:</span> <strong>${jobSheet.customer_name}</strong></td>
              <td style="padding: 4px;"><span class="label">Part Number:</span> <strong>${jobSheet.part_description || 'N/A'}</strong></td>
            </tr>
            <tr>
              <td style="padding: 4px;"><span class="label">Total Quantity:</span> <strong>${jobSheet.quantity}</strong></td>
              <td style="padding: 4px;"><span class="label">Inspection Interval:</span> <strong>${inspectionInterval ? `Every ${inspectionInterval} parts` : 'Not specified'}</strong></td>
            </tr>
          </table>
        </div>
        
        <div class="section">
          <h2 style="margin: 0 0 8px 0; font-size: 11pt; color: #333;">Inspection Record</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #f3f4f6;">
                <th style="border: 1px solid #333; padding: 6px; text-align: left; font-size: 9pt;">Check Item</th>
                <th style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; width: 60px;">Nominal</th>
                <th style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; width: 55px;">Tol.</th>
                <th style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; width: 60px; background: #fef3c7;">Min</th>
                <th style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; width: 60px; background: #fef3c7;">Max</th>
                ${(() => {
                  const qty = jobSheet.quantity || 1;
                  const interval = inspectionInterval || Math.ceil(qty / 5);
                  const checkpoints = ['1st Off'];
                  for (let i = interval; i < qty; i += interval) {
                    checkpoints.push(i);
                  }
                  if (qty > 1) {
                    checkpoints.push(qty);
                  }
                  return checkpoints.map(cp => `<th style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; width: 55px;">${cp === '1st Off' ? cp : 'Qty ' + cp}</th>`).join('');
                })()}
              </tr>
            </thead>
            <tbody>
              ${qaInspectionItems.map(item => {
                const tolStr = String(item.tolerance ?? '');
                const tolMatch = tolStr.match(/[-+]?\d*\.?\d+/);
                const tolNum = tolMatch ? Math.abs(parseFloat(tolMatch[0])) : 0;
                const nominal = parseFloat(item.nominal_value) || 0;
                const minVal = nominal - tolNum;
                const maxVal = nominal + tolNum;
                const unit = item.type === 'angle' ? '°' : ' mm';
                const fmt = (n) => Number.isFinite(n) ? (Number.isInteger(n) ? n : Number(n.toFixed(3))) : '—';
                return `
                <tr>
                  <td style="border: 1px solid #333; padding: 6px; font-size: 9pt;">
                    ${item.type === 'angle' ? '∠' : '↔'} ${item.description || 'Unnamed'}
                  </td>
                  <td style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt;">
                    ${item.nominal_value}${unit}
                  </td>
                  <td style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 8pt;">
                    ${item.tolerance || '±0.5'}
                  </td>
                  <td style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; background: #fefce8; font-weight: bold;">
                    ${fmt(minVal)}${unit}
                  </td>
                  <td style="border: 1px solid #333; padding: 6px; text-align: center; font-size: 9pt; background: #fefce8; font-weight: bold;">
                    ${fmt(maxVal)}${unit}
                  </td>
                  ${(() => {
                    const qty = jobSheet.quantity || 1;
                    const interval = inspectionInterval || Math.ceil(qty / 5);
                    const checkpoints = ['1st Off'];
                    for (let i = interval; i < qty; i += interval) {
                      checkpoints.push(i);
                    }
                    if (qty > 1) {
                      checkpoints.push(qty);
                    }
                    return checkpoints.map(() => `<td style="border: 1px solid #333; padding: 6px; text-align: center; min-height: 25px;"></td>`).join('');
                  })()}
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="section" style="margin-top: 15px;">
          <h2 style="margin: 0 0 8px 0; font-size: 11pt; color: #333;">Operator Sign-Off</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 6px; width: 33%;"><span class="label">Inspector Name:</span> <span class="underline-field" style="min-width: 150px;"></span></td>
              <td style="padding: 6px; width: 33%;"><span class="label">Date:</span> <span class="underline-field" style="min-width: 100px;"></span></td>
              <td style="padding: 6px; width: 34%;"><span class="label">Signature:</span> <span class="underline-field" style="min-width: 150px;"></span></td>
            </tr>
          </table>
          <div style="margin-top: 10px; padding: 8px; background: #fef3c7; border: 1px solid #d97706; border-radius: 4px;">
            <p style="margin: 0; font-size: 8pt; color: #92400e;">
              <strong>Instructions:</strong> Record actual measurements at each quantity checkpoint. 
              All dimensions in mm, angles in degrees (°). 
              If any measurement is out of tolerance, stop production and notify supervisor immediately.
            </p>
          </div>
        </div>
        ` : ''}

        <div style="margin-top: 10px; text-align: center;">
          <button onclick="window.print()" style="padding: 8px 24px; background: #333; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 11pt;">
            🖨️ Print${qaInspectionItems && qaInspectionItems.length > 0 ? ' (2 Pages)' : ''}
          </button>
        </div>
      </body>
    </html>
  `);
  printWindow.document.close();
};
